/* eslint-disable no-console */
import * as constants from '../constants/websocket';
import {
  endUserJoinedChat,
  setChatParticipantsFromEvent,
  newMessage,
  inlineMessageUpdate,
  needsActionUpdate,
} from '../actions/chat';
import { showSnackbar } from '../actions/notification';
import { addBulkUser, addUser, modifiedUser } from '../actions/users';
import { assignUser, updateJob } from '../actions/job';
import { addItem, modifyItem } from '../actions/common';
import {
  CREATE_NOTE,
  DELETE_NOTE,
  DELETE_REPLY,
  ADD_REPLY,
  NEW_MESSAGE,
  UPDATED_USER,
  REDACT_IMAGE,
} from '../constants/actions';
import { NEEDS_ATTENTION, NEEDS_ACTION } from '../constants/permissions';
import globalMediaCacheFactory from '../hooks/global-file-cache';
import { REDACTED_IMAGE } from '../constants/general';
import User from '../models/user';
import { s3toPipedUrl } from './urls';
import { JOB_RELATED_EVENTS } from './job-event-processor';
import { deauthUserAndUnsubscribeTopics } from '@app/actions/auth-utils';
import Message from '@app/models/message';
import handleChatMessageItem, {
  handleDismissNeedsAction,
  handleDismissAllNeedsAttention,
} from '@app/cache/chat-messages/chat-cache';
import {
  getWebSocketPayloadArray,
  parseEventPayload,
} from '@app/helpers/websocket-event-payload';
import { QUERY_KEYS } from '@app/constants/service';
import showBulkQueueRunCompleteToast from '@app/helpers/bulk-queue-toasts';
import {
  setBulkQueueResultRun,
  signalBulkQueueCasesChanged,
} from '@app/services/bulk-queue';
import {
  showCaseExposureRebuildCompleteToast,
  showCaseExposureRebuildFailedToast,
} from '@app/helpers/exposures/case-exposure-rebuild-toasts';
import { settleExposureRebuild } from '@app/helpers/exposures/exposure-rebuild-progress';
import i18n from '@app/i18n';
import {
  clearTemplateRecommendations,
  invalidateTemplateRecommendations,
} from '@app/cache/template-recommendations';
import { TEMPLATE_RECOMMENDATION } from '@app/constants/feature-definitions';
import { MESSAGE_SOURCE } from '@app/constants/messages';
import { isFeatureEnabledFromCache } from '@app/services/organization-features';
const jobAssignedEvent = (data) => assignUser(data);
const newNoteEvent = (data) => (dispatch) =>
  dispatch({ type: CREATE_NOTE, payload: data });
const updateGroupEvent = (data) => modifyItem('GROUP')(data);
const createGroupEvent = (data) => addItem('GROUP')(data);
const deleteNoteEvent = (data) => (dispatch) =>
  dispatch({ type: DELETE_NOTE, payload: data });
const newReplyEvent = (data) => (dispatch) =>
  dispatch({ type: ADD_REPLY, payload: data });
const deleteReplyEvent = (data) => (dispatch) =>
  dispatch({ type: DELETE_REPLY, payload: data });

export const newUserMentionedEvent = (data) =>
  showSnackbar({
    text: data?.noteText,
    linkText: i18n.t('common:view'),
    isError: false,
    linkID: `/chats/${data?.caseId}`,
    title: i18n.t('notes:mentionedInNote'),
    isHyperLink: true,
    persist: true,
    jobId: data?.caseId,
  });

export const newJobEvent = (data, _queryClient, _eventName) => (dispatch) => {
  dispatch(addItem('CASE')(data));
  // React Query cache updates handled by job-event-processor.ts
};

const updateJobEvent = (data, _queryClient, _eventName) => (dispatch) => {
  dispatch(updateJob(data));
  // React Query cache updates handled by job-event-processor.ts
};

export const shouldSkipMessageUpdate = (lastViewedMessage, message) => {
  if (lastViewedMessage?.messageId === message?._id) {
    return true;
  }
  if (lastViewedMessage?.timestamp && message?.updatedAt) {
    const lastViewedMessageTimestamp = new Date(
      lastViewedMessage?.timestamp,
    ).getTime();
    const messageUpdateAtTimestamp = new Date(message?.updatedAt).getTime();
    if (
      Number.isNaN(lastViewedMessageTimestamp) ||
      Number.isNaN(messageUpdateAtTimestamp)
    ) {
      return false;
    }
    if (lastViewedMessageTimestamp >= messageUpdateAtTimestamp) {
      return true;
    }
  }
  return false;
};

const isTemplateRecommendationEnabled = (queryClient) =>
  isFeatureEnabledFromCache(queryClient, TEMPLATE_RECOMMENDATION, {
    failClosed: true,
  });

const ANSWERING_SOURCES = [MESSAGE_SOURCE.OPERATOR, MESSAGE_SOURCE.AI_AGENT];

const answersCase = (message) =>
  ANSWERING_SOURCES.includes(message?.source) ||
  message?.source === null ||
  message?.source === undefined;

const changesTemplateRecommendation = (message, queryClient) =>
  !message?.isEvent &&
  !message?.isNote &&
  (message?.isInbound || answersCase(message)) &&
  isTemplateRecommendationEnabled(queryClient);

const newMessageEvent =
  (data, queryClient, _eventName) => (dispatch, getStore) => {
    dispatch(newMessage(data));
    const store = getStore();
    handleChatMessageItem(
      data?.chatId,
      new Message(data),
      NEW_MESSAGE,
      queryClient,
      store,
    );
    // Only media messages affect the media tab; skip the refetch for ordinary
    // text traffic so a busy chat doesn't churn /mediamessages on every event.
    if (data?.fileUrl) {
      invalidateChatMedia(data?.chatId, queryClient);
    }
    // Invalidate quiet hours cache only for the active case so the
    // banner/send lock clears when the deferred welcome message is sent
    const activeJobId = store?.jobs?.activeChat?.jobId;
    if (activeJobId && data?.jobId === activeJobId) {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.QUIET_HOURS_TASKS(activeJobId),
      });
    }
    if (changesTemplateRecommendation(data, queryClient)) {
      clearTemplateRecommendations(data?.jobId, queryClient);
      invalidateTemplateRecommendations(data?.jobId, queryClient);
    }
  };

const templateRecommendationEvent = (data, queryClient) => () => {
  if (isTemplateRecommendationEnabled(queryClient)) {
    invalidateTemplateRecommendations(data?.jobId, queryClient);
  }
};

const dismissNeedsActionEvent = (data, queryClient) => (dispatch, getStore) => {
  const { job } = data;
  const store = getStore();
  handleDismissNeedsAction(job?.primaryChatId, queryClient, store);
};

const dismissNeedsAttentionReplyEvent =
  (data, queryClient) => (dispatch, getStore) => {
    const { job } = data;
    const store = getStore();
    handleDismissAllNeedsAttention(job?.primaryChatId, queryClient, store);
  };

const dismissNeedsAttentionEvent =
  (data, queryClient) => (dispatch, getStore) => {
    const { message } = data;
    const store = getStore();
    handleChatMessageItem(message?.chatId, message, '', queryClient, store);
  };

/**
 * Function to check if we should redact the live update message event
 * @param {*} messageEvent message object coming in from event
 * @param {*} localMessage message object in redux store
 * @param {*} authId id of logged in user from redux
 * @returns {Boolean} on whether the image should be redacted
 */
export const redactImageMessageEvent = (messageEvent, authId) => {
  const redactionDataItem = messageEvent?.redactionData?.find(
    (r) => r.Type === REDACTED_IMAGE,
  );
  const createdBy = redactionDataItem?.createdBy;
  return Boolean(authId && redactionDataItem && createdBy);
};

export const updateMessageEvent =
  (data, queryClient, _eventName) => async (dispatch, getStore) => {
    const store = getStore();
    const authId = store.auth.user._id || store.auth.user.id;
    const redactImageForUser = redactImageMessageEvent(data.newMessage, authId);
    let { newMessage: updatedMessage } = data || {};
    if (redactImageForUser) {
      const { fileUrl, redactionData } = data.newMessage;
      const pipedUrl = s3toPipedUrl(fileUrl);
      const globalFileCache = await globalMediaCacheFactory();
      await globalFileCache?.deleteItem(pipedUrl);
      updatedMessage = {
        ...data.newMessage,
        fileUrl: pipedUrl,
        redactionData,
      };
      dispatch({
        type: REDACT_IMAGE,
        payload: {
          fileUrl: pipedUrl,
          messageId: updatedMessage._id,
          redactionData,
        },
      });
    }
    handleChatMessageItem(
      updatedMessage?.chatId,
      new Message(updatedMessage),
      '',
      queryClient,
      store,
    );
    // Redactions and virus-scan transitions arrive on the media message
    // itself (it carries a fileUrl); text-message edits do not, so gating on
    // fileUrl keeps non-media updates from refetching /mediamessages.
    if (updatedMessage?.fileUrl) {
      invalidateChatMedia(updatedMessage?.chatId, queryClient);
    }
  };

export const inlineMessageUpdateEvent =
  (data, queryClient, _eventName) => (dispatch, getStore) => {
    const store = getStore();
    const userHasAccess = store?.auth?.permissions.includes(NEEDS_ATTENTION);
    if (userHasAccess) {
      const message = new Message(data?.message || data?.newMessage);
      handleChatMessageItem(message?.chatId, message, '', queryClient, store);
      dispatch(inlineMessageUpdate(data));
    }
  };

export const updatedNeedsActionEvent =
  (data, queryClient, _eventName) => (dispatch, getStore) => {
    const store = getStore();
    const userHasAccess = store?.auth?.permissions.includes(NEEDS_ACTION);
    if (userHasAccess) {
      const message = new Message(data?.message || data?.newMessage);
      handleChatMessageItem(message?.chatId, message, '', queryClient, store);
      dispatch(needsActionUpdate(data));
    }
  };

const handleLogoutUser = () => (dispatch) => {
  dispatch(deauthUserAndUnsubscribeTopics());
};

const handleModifiedUser = (data, queryClient) => (dispatch, getStore) => {
  const store = getStore();
  dispatch(modifyItem('OPERATOR')(data));
  dispatch(modifiedUser(data));
  handleChatMessageItem(
    data?.chatId,
    new Message(data),
    UPDATED_USER,
    queryClient,
    store,
    new User(data),
  );
};

export const handleUpdateInboxFiltersEvent =
  (data, queryClient, name, userId) => (dispatch, getStore) => {
    const store = getStore();
    if (userId !== store?.auth?.user?._id) {
      return;
    }
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.USER_PROPERTIES(userId),
    });
    dispatch({
      type: 'UPDATE_PROFILE_FILTERS',
      payload: { inboxFilters: data },
    });
  };

export const handleUpdateInboxBehaviorEvent =
  (data, queryClient, name, userId) => (dispatch, getStore) => {
    const store = getStore();
    if (userId !== store?.auth?.user?._id) {
      return;
    }
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.USER_PROPERTIES(userId),
    });
    dispatch({
      type: 'UPDATE_PROFILE_FILTERS',
      payload: { inboxBehavior: data },
    });
  };

const addTranslationEvent = (data, queryClient) => (dispatch, getStore) => {
  const store = getStore();
  handleChatMessageItem(
    data?.chatId,
    new Message(data),
    '',
    queryClient,
    store,
  );
};

/**
 * Get user permissions
 * @param {*} organizationName Name of users organzation
 * @param {*} userId Id of user
 * @returns {Array[string]} array of a users permissions
 */
const eventMap = {};

eventMap[constants.NEW_USER] = addUser;
eventMap[constants.NEW_BULK_USER] = addBulkUser;
eventMap[constants.UPDATED_USER] = handleModifiedUser;
eventMap[constants.NEW_MESSAGE] = newMessageEvent;
eventMap[constants.TEMPLATE_RECOMMENDATION_EVENT] = templateRecommendationEvent;
eventMap[constants.USER_ASSIGNED] = jobAssignedEvent;
eventMap[constants.ADD_TRANSLATION] = addTranslationEvent;
eventMap[constants.NEW_JOB] = newJobEvent;
eventMap[constants.UPDATED_JOB] = updateJobEvent;
eventMap[constants.CREATE_NOTE] = newNoteEvent;
eventMap[constants.UPDATE_GROUP] = updateGroupEvent;
eventMap[constants.CREATE_GROUP] = createGroupEvent;
eventMap[constants.USER_MENTIONED] = newUserMentionedEvent;
// Dynamically imported to keep papaparse out of the main bundle — this handler
// is only needed after a bulk user upload completes via websocket.
eventMap[constants.USER_OKTA_INSERTION_FINISH] =
  (data, _queryClient, _eventName, _userId) => async (dispatch) => {
    const { handleBulkUploadOktaComplete } = await import(
      '../middlewares/bulk-user-upload-handler'
    );
    await handleBulkUploadOktaComplete(data)(dispatch);
  };
eventMap[constants.DELETE_NOTE] = deleteNoteEvent;
eventMap[constants.INLINE_MESSAGE_UPDATE] = inlineMessageUpdateEvent;
eventMap[constants.UPDATED_NEEDS_ACTION] = updatedNeedsActionEvent;
eventMap[constants.UPDATE_MESSAGE] = updateMessageEvent;
eventMap[constants.CHAT_END_USER_REMOVED] = setChatParticipantsFromEvent;
eventMap[constants.CHAT_END_USER_JOINED] = endUserJoinedChat;
eventMap[constants.ADD_REPLY] = newReplyEvent;
eventMap[constants.DELETE_REPLY] = deleteReplyEvent;
eventMap[constants.LOGOUT_USER] = handleLogoutUser;
eventMap[constants.UPDATE_INBOX_FILTERS] = handleUpdateInboxFiltersEvent;
eventMap[constants.UPDATE_INBOX_BEHAVIOR] = handleUpdateInboxBehaviorEvent;
eventMap[constants.DISMISS_NEEDS_ACTION] = dismissNeedsActionEvent;
eventMap[constants.DISMISS_NEEDS_ATTENTION_REPLY] =
  dismissNeedsAttentionReplyEvent;
eventMap[constants.DISMISS_NEEDS_ATTENTION] = dismissNeedsAttentionEvent;
// Both events ride the same channel for old date-picker scheduled messages
// AND quiet-hours-deferred sends. The reducer gates on `triggerType` so QH
// events don't pollute `state.jobs.scheduledMessages`; on the React Query
// side we invalidate `QUIET_HOURS_TASKS` for the affected case so the QH
// drawer refreshes for every operator subscribed to the chat topic.
// Invalidate the media query for the chat the media belongs to — NOT only the
// active chat. Media can arrive for a chat the user isn't viewing; invalidating
// its (likely inactive) query marks it stale so it refetches when the user next
// opens that chat, instead of serving a cached list missing the new media. The
// active chat's query is observed, so it refetches immediately.
export const invalidateChatMedia = (chatId, queryClient) => {
  if (chatId && queryClient) {
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.CHAT_MEDIA(chatId),
    });
  }
};

const invalidateQuietHoursTasksForJob = (data, queryClient) => {
  if (data?.triggerType !== 'quiet-hours') return;
  const jobId = data?.scheduledMessage?.jobId;
  if (!jobId || !queryClient) return;
  queryClient.invalidateQueries({
    queryKey: QUERY_KEYS.QUIET_HOURS_TASKS(jobId),
  });
};

eventMap[constants.SCHEDULED_MESSAGE_CREATED] =
  (data, queryClient) => (dispatch) => {
    dispatch({ type: 'SCHEDULED_MESSAGE_CREATED', payload: data });
    invalidateQuietHoursTasksForJob(data, queryClient);
  };
eventMap[constants.SCHEDULED_MESSAGE_CANCELLED] =
  (data, queryClient) => (dispatch) => {
    dispatch({ type: 'SCHEDULED_MESSAGE_CANCELLED', payload: data });
    invalidateQuietHoursTasksForJob(data, queryClient);
  };

// The submitter is not guaranteed to be on the bulk queue topic, so signal from here too.
export const bulkQueueRunCompleteEvent = (data, queryClient) => async () => {
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BULK_QUEUE_STATUS] });
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CASES] });
  signalBulkQueueCasesChanged(queryClient, data?.runId);
  await showBulkQueueRunCompleteToast(data, () =>
    setBulkQueueResultRun(queryClient, data?.runId ?? null),
  );
};

// Carries no counts on purpose: another admin's run totals never go org-wide.
export const bulkQueueCasesChangedEvent = (data, queryClient) => () => {
  // Every other operator gets only this event; without it their status stays stale 30s.
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BULK_QUEUE_STATUS] });
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CASES] });
  signalBulkQueueCasesChanged(queryClient, data?.runId);
};

eventMap[constants.BULK_CASE_ACTION_RUN_COMPLETE] = bulkQueueRunCompleteEvent;
eventMap[constants.BULK_CASE_ACTION_CASES_CHANGED] = bulkQueueCasesChangedEvent;

export const caseExposureRebuildCompleteEvent = (data, queryClient) => () => {
  settleExposureRebuild(data?.rebuildId);
  // The admin may still be on the exposures screen and the counts there are now stale.
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EXPOSURES] });
  showCaseExposureRebuildCompleteToast(data).catch((err) => {
    console.error('case exposure rebuild toast failed');
    console.error(err);
  });
};

eventMap[constants.CASE_EXPOSURE_REBUILD_COMPLETE] =
  caseExposureRebuildCompleteEvent;

export const caseExposureRebuildFailedEvent = (data) => () => {
  settleExposureRebuild(data?.rebuildId);
  showCaseExposureRebuildFailedToast(data).catch((err) => {
    console.error('case exposure rebuild failure toast failed');
    console.error(err);
  });
};

eventMap[constants.CASE_EXPOSURE_REBUILD_FAILED] =
  caseExposureRebuildFailedEvent;

export const eventProcessor = (dispatch, queryClient) => (messageEvent) => {
  const payload = getWebSocketPayloadArray(messageEvent);
  if (!payload) return;
  payload.forEach((event) => {
    const eventHandler = eventMap[event.name];
    if (eventHandler) {
      try {
        const data = parseEventPayload(event.payload);
        const handled = dispatch(
          eventHandler(data, queryClient, event.name, event?.userId),
        );
        // An async handler rejects after dispatch returns, past this try/catch.
        if (typeof handled?.catch === 'function') {
          handled.catch((err) => {
            console.error('event handler failed for: ', event.name);
            console.error(err);
          });
        }
      } catch (err) {
        console.error('cannot parse payload: ', event.payload);
        console.error(err);
      }
    } else {
      // Only log unknown events that aren't handled by job-event-processor.ts
      if (!JOB_RELATED_EVENTS.has(event.name)) {
        console.error('cannot find event handler for: ', event);
      }
    }
  });
};
